import { useContext, Fragment, Suspense, useState } from "react"
import { Helmet } from "@dr.pogodin/react-helmet"
import NoLogoPlaceholder from "./noLogoPlaceholder"
import { useApiRead } from "source/shared/SessionApiResource"
import { Loading, Icon, Card, CardGrid } from "source/shared/components"
import { Link, useNavigate } from "react-router"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { QueryFunctionContext, useSuspenseQueries } from "@tanstack/react-query"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { useQRCode } from "source/shared/QRCode"
import { useCustomNavigationLabel } from "source/shared/hooks/useCustomNavigationLabel"
import { SuccessMessage } from "./SuccessMessage"

const API_VERSION = "2020-06-16"

type Campus = {
  id: string
  attributes: { name: string }
}

type Category = {
  id: string
  attributes: { name: string }
}

type Event = {
  id: string
  attributes: {
    name: string
    logo_url: string
    event_time: string
    featured: boolean
    registration_state: string
    starts_at: string
    ends_at: string
  }
}

type EventsProps = {
  campusId?: string
  categoryId?: string
}

const Events = ({ campusId, categoryId }: EventsProps) => {
  const results = useSuspenseQueries({
    queries: [
      {
        queryKey: [
          "/registrations/v2/categories?order=name&per_page=100",
          { version: API_VERSION },
        ],
        queryFn: ({ queryKey }: QueryFunctionContext) =>
          sessionApiClient
            .get(queryKey[0], {}, queryKey[1])
            .catch(() => ({ data: [] })),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: [
          "/registrations/v2/campuses?filter=with_listed_events&order=name&per_page=100",
          { version: API_VERSION },
        ],
        queryFn: ({ queryKey }: QueryFunctionContext) =>
          sessionApiClient
            .get(queryKey[0], {}, queryKey[1])
            .catch(() => ({ data: [] })),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  })

  const categories: Category[] = results[0].data?.data || []
  const campuses: Campus[] = results[1].data?.data || []
  useQRCode()

  let selectedCampus: Campus | undefined
  let selectedCategory: Category | undefined

  if (campusId || categoryId) {
    selectedCampus = campusId
      ? campuses.find((campus) => {
          return campus.id === campusId
        })
      : undefined
    selectedCategory = categoryId
      ? categories.find((category) => {
          return category.id === categoryId
        })
      : undefined
  }

  const filter = [
    "order=starts_at",
    "filter=unarchived,published",
    "fields[Event]=name,featured,logo_url,event_time,starts_at,ends_at,registration_state",
    "per_page=100",
  ]
  if (selectedCampus) {
    filter.push(`where[campus]=${encodeURIComponent(selectedCampus.id)},0`)
  }

  if (selectedCategory) {
    filter.push(
      `where[category]=${encodeURIComponent(selectedCategory.attributes.name)}`,
    )
  }

  const urlParams = new URLSearchParams(window?.location?.search)
  const unsub = urlParams.get("unsub")

  return (
    <Fragment>
      <MetaInformationForDocumentHeader />
      <FilterBar
        campuses={campuses}
        categories={categories}
        campus={selectedCampus}
        category={selectedCategory}
      />
      {unsub && (
        <SuccessMessage>
          You have been removed from future notifications for this signup.
        </SuccessMessage>
      )}
      <Suspense fallback={<Loading />}>
        <EventList filter={filter} />
      </Suspense>
    </Fragment>
  )
}

type EventListProps = {
  filter?: string[]
}

const EventList = ({ filter = [] }: EventListProps) => {
  const path = ["/registrations/v2/events", filter.join("&")]
    .filter(Boolean)
    .join("?")

  const { data: events = [] } = useApiRead(path, { version: "2020-06-16" }) as {
    data: Event[]
  }

  return events.length > 0 ? (
    <CardGrid>
      {events.map((event, index) => {
        return <EventItem event={event} key={index} />
      })}
    </CardGrid>
  ) : (
    <NoEventsPlaceholder />
  )
}
const MetaInformationForDocumentHeader = () => {
  const {
    layout: {
      organization_name: organizationName,
      organization_avatar_url: avatarUrl,
    },
  } = useContext(ServerRenderedProps)
  const title = useCustomNavigationLabel("Events")

  return (
    <Helmet>
      <title>{title}</title>
      <meta property="og:title" content="Events" />
      <meta
        property="og:description"
        content={`Events at ${organizationName}`}
      />
      <meta property="og:image" content={avatarUrl} />
    </Helmet>
  )
}

type FilterBarProps = {
  campuses: Campus[]
  categories: Category[]
  campus?: Campus
  category?: Category
}

const FilterBar = ({
  campuses,
  categories,
  campus,
  category,
}: FilterBarProps) => {
  const navigate = useNavigate()

  const defaultCampusFilter = "All Campuses"
  const defaultCategoryFilter = "All Categories"
  const selectedCampus = campus ? campus.attributes.name : defaultCampusFilter
  const selectedCategory = category
    ? category.attributes.name
    : defaultCategoryFilter

  const handleCampusChange = (e: string) => {
    handleFilterChange({ campus: e, category: selectedCategory })
  }

  const handleCategoryChange = (e: string) => {
    handleFilterChange({ campus: selectedCampus, category: e })
  }

  const handleFilterChange = ({
    campus,
    category,
  }: {
    campus: string
    category: string
  }) => {
    let path = "/registrations/events"

    if (campus && campus != defaultCampusFilter) {
      const matchedCampus = campuses.find((c) => c.attributes.name === campus)
      if (matchedCampus) {
        path += `/campus/${matchedCampus.id}`
      }
    }

    if (category && category != defaultCategoryFilter) {
      const matchedCategory = categories.find(
        (c) => c.attributes.name === category,
      )
      if (matchedCategory) {
        path += `/category/${matchedCategory.id}`
      }
    }

    navigate(path)
  }

  return (
    <div className="action-drawer mb-2 mb-4@md d-f@sm ai-fs">
      <div className="d-b d-f@sm fw-w mb-1 mb-0@sm mr-1@sm">
        {campuses.length > 1 && (
          <Filter
            items={campuses.map((campus) => {
              return campus.attributes.name
            })}
            selected={selectedCampus}
            selectedChanged={handleCampusChange}
            defaultFilter={defaultCampusFilter}
          />
        )}
      </div>
      <div className="d-b d-f@sm fw-w">
        {categories.length > 0 && (
          <Filter
            items={categories.map((category) => {
              return category.attributes.name
            })}
            selected={selectedCategory}
            selectedChanged={handleCategoryChange}
            defaultFilter={defaultCategoryFilter}
          />
        )}
      </div>
    </div>
  )
}

type FilterProps = {
  items: string[]
  defaultFilter: string
  selected: string
  selectedChanged?: (value: string) => void
}

const Filter = ({
  items,
  defaultFilter,
  selected,
  selectedChanged = () => {},
}: FilterProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggle = () => setIsOpen(!isOpen)
  const onSelect = (clicked: string) => {
    if (clicked !== selected) {
      selectedChanged(clicked)
    }
    setIsOpen(false)
  }

  return (
    <div className={`dropdown ${isOpen ? "is-open" : ""}`}>
      <button className="select ta-l" onClick={toggle}>
        <span>{selected}</span>
        <span className="caret c-primary" />
      </button>
      <ul className="dropdown__menu">
        <li className="dropdown__item p-0">
          <button
            style={{
              display: "block",
              color: "#424242",
              lineHeight: "1.25",
              padding: "0.5rem 0.75rem",
              backgroundColor: "transparent",
              border: "none",
              fontWeight: 400,
              textAlign: "left",
              width: "100%",
            }}
            onClick={() => {
              onSelect(defaultFilter)
            }}
          >
            {defaultFilter}
          </button>
        </li>
        {items.map((item, index) => {
          return (
            <li key={index} className="dropdown__item p-0">
              <button
                style={{
                  display: "block",
                  color: "#424242",
                  lineHeight: "1.25",
                  padding: "0.5rem 0.75rem",
                  backgroundColor: "transparent",
                  border: "none",
                  fontWeight: 400,
                  textAlign: "left",
                  width: "100%",
                  whiteSpace: "nowrap",
                }}
                onClick={() => {
                  onSelect(item)
                }}
              >
                {item}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export const NoEventsPlaceholder = () => (
  <div className="ta-c c-tint2 fs-2">
    <Icon symbol="churchCenter#registrations-o" className="mb-1" aria-hidden />
    <div>Looks like there aren&apos;t any events available</div>
  </div>
)

type EventItemProps = {
  event: Event
}

export const EventItem = ({ event }: EventItemProps) => {
  const {
    attributes: {
      logo_url: logoURL,
      name,
      event_time: eventTimeSummary,
      featured,
    },
  } = event

  return (
    <Card
      // Card's link type doesn't account for polymorphic props passed via `as`
      link={
        {
          as: Link,
          to: `/registrations/events/${event.id}`,
          state: { event },
        } as Record<string, unknown>
      }
      heading={name}
      description={eventTimeSummary}
      poster={
        logoURL ? (
          <img loading="lazy" src={logoURL} alt={`${name} logo`} />
        ) : (
          <NoLogoPlaceholder event={event} name={name} />
        )
      }
      badges={[featured && { theme: "featured", label: "Featured" }]}
    />
  )
}

export default Events
